import React, { useState } from 'react';
import { Organization, OrganizationListObj, SpotInterface } from 'types';
import Arrow from 'assets/icons/triangle.svg?react';
import styles from './SpotOrganizationSelector.module.css';
import { Menu, MenuHeader, MenuItem, Submenu } from 'components/Menu/Menu';
import { useOrganization } from 'contexts/OrganizationsContext';
import { DeviceHubIcon } from 'components/Icons/DeviceHubIcon';
import { LanIcon } from 'components/Icons/LanIcon';

type SpotOrganizationSelectorProps = {
    currentOrganization: Organization;
    currentSpot: SpotInterface;
    spots: SpotInterface[];
    isDemoMode?: boolean;
    onSelectOrganization: (organization: OrganizationListObj) => void;
    onSelectSpot: (spot: SpotInterface) => void;
};

function IconWrapper({ size = 38, children }: React.PropsWithChildren<{ size?: number }>) {
    return (
        <div
            style={{
                width: size,
                height: size,
            }}
            className={styles.iconWrapper}
        >
            {children}
        </div>
    );
}

function SpotOrganizationSelector({
    currentOrganization,
    currentSpot,
    spots,
    isDemoMode,
    onSelectOrganization,
    onSelectSpot,
}: SpotOrganizationSelectorProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const { organizations } = useOrganization();

    const trigger = (
        <div className={styles.triggerContainer}>
            <div className={`${styles.triggerContent} ${isDemoMode ? 'blur' : ''}`}>
                <span className={styles.spotName}>{currentSpot.name}</span>
                <Arrow className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`} />
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            <Menu
                trigger={trigger}
                open={isOpen}
                onToggle={setIsOpen}
                width="fit-content"
                className={styles.menuContainer}
                menuClassName={styles.menu}
                openOnHover={!isDemoMode}
            >
                <MenuHeader
                    icon={
                        <IconWrapper>
                            <DeviceHubIcon color="#FF6B35" width="18" height="18" />
                        </IconWrapper>
                    }
                    subtext="Organization"
                >
                    {currentOrganization.name}
                </MenuHeader>

                {spots.map((spot) => (
                    <MenuItem key={spot.pk} selected={spot.pk === currentSpot.pk} onClick={() => onSelectSpot(spot)}>
                        <h5 className={styles.menuSpotHeader}>
                            {spot.address.address_line1}, {spot.address.city}, {spot.address.state}
                        </h5>
                        <span className={styles.menuSpotSubheader}>{spot.name}</span>
                    </MenuItem>
                ))}

                {organizations && organizations.length > 1 && (
                    <Submenu trigger="Switch Organization">
                        {organizations.map((org) => (
                            <MenuItem
                                key={org.pk}
                                icon={
                                    <IconWrapper size={32}>
                                        <LanIcon color="#FF6B35" width="15" height="15" />
                                    </IconWrapper>
                                }
                                selected={org.pk === currentOrganization.pk}
                                onClick={() => onSelectOrganization(org)}
                            >
                                {org.name}
                            </MenuItem>
                        ))}
                    </Submenu>
                )}
            </Menu>
            <div className={styles.fullAddress}>
                <span className={isDemoMode ? 'blur' : ''}>{currentSpot.address.address_line1},</span>{' '}
                {currentSpot.address.city}, {currentSpot.address.state}
            </div>
        </div>
    );
}

export default SpotOrganizationSelector;
